import {getLocation} from 'common/util/url';
import {getPersistedSearch, getReportName, IronAppInfo, isPathBasedApp, urlToIronAppInfo} from 'common/report/util';
import {shouldOpenInNewTab} from 'common/util/dom';
import {parseStateFromUrl} from 'common/report/util/state-from-url';
import {diffFromNow, getNow} from 'common/util/timing';
import {viewedReportFlagManager} from 'common/report/queries/app-api/user-flags';
import {waitUntilUiReady} from 'common/report/perf-measure';
import {clearAllToasts} from 'common/components/toast-area/toast-manager';
import {ReportName} from 'common/report/constants';
import {trackVisitedReport, VisitedReportReason} from 'common/util/server-tracking';
import {SubscribableContext} from 'common/util/model-resource/subscribable-context';

import {NavigateParams, NavManager, Props} from './types';

export class SpaNavManager extends SubscribableContext implements NavManager {
  private spaNavCount = 0;
  private declare props: Props;

  updateProps(props: Props) {
    this.props = {...props};
    this.notifySubscribers();
  }

  private getIronAppInfo(urlPath: string): IronAppInfo | undefined {
    const urlState = parseStateFromUrl(urlPath);
    if (!urlState) {
      return undefined;
    }
    return urlToIronAppInfo[urlState.appName] || undefined;
  }

  getSpaNavCount() {
    return this.spaNavCount;
  }

  private isSpaNavigable({
    currentUrlPath,
    destinationUrlPath,
  }: {
    destinationUrlPath: string;
    currentUrlPath: string;
  }): boolean {
    const currentUrlState = parseStateFromUrl(currentUrlPath);
    const destinationUrlState = parseStateFromUrl(destinationUrlPath);
    const currentApp = this.getIronAppInfo(currentUrlPath);
    const destinationApp = this.getIronAppInfo(destinationUrlPath);

    if (!currentApp || !destinationApp || !currentUrlState || !destinationUrlState) {
      return false;
    }

    if (destinationApp && isPathBasedApp(destinationApp.appName)) {
      return true;
    }

    // If you try to go to the current app, the actual behavior is to refresh the page.
    return Boolean(
      currentApp.appName !== destinationApp.appName &&
        currentUrlState.projectId === destinationUrlState.projectId &&
        currentUrlState.workspaceId === destinationUrlState.workspaceId,
    );
  }

  /**
   * track 'Viewed Report' and 'Report Loaded' events with 'is_spa_nav' flag so we can pretend a pageload occurred
   */
  private trackReportLoaded({
    reportName,
    elapsedMs,
    keepReportContext,
  }: {
    reportName: ReportName;
    elapsedMs: number;
    keepReportContext?: boolean;
  }) {
    const projectId = this.props.currentProjectResource.id;
    const commonTrackProps = {
      project_id: projectId,
      is_spa_nav: true,
      tab: reportName,
      keptReportContext: keepReportContext,
    };

    mixpanel.track(`Viewed report`, commonTrackProps);

    trackVisitedReport({
      projectId,
      reportName,
      reason: VisitedReportReason.ReportVoltNavigate,
    });

    mixpanel.track(`Report Loaded`, {
      ...commonTrackProps,
      preloadedAssets: true,
      page_time_to_ui_ready_ms: elapsedMs,
    });

    mixpanel.infra_metrics.track(`report-loaded`, {
      ...commonTrackProps,
      distinct_id: projectId,
      preloadedAssets: true,
      page_time_to_ui_ready_ms: elapsedMs,
    });
  }

  async navigate(
    url: string,
    {triggerEvent, fromAnchorLink, anchorTarget, keepReportContext}: NavigateParams = {},
  ): Promise<void> {
    if (anchorTarget === `_blank`) {
      // let browser handle open in new tab interaction for a(target=_blank)
      return;
    }

    const urlObj = new URL(url, getLocation().origin);
    urlObj.search = getPersistedSearch(getLocation().search);
    const currentUrlPath = location.pathname;
    const destinationUrlPath = urlObj.pathname;
    const destinationUrl = urlObj.toString();

    // If we are keeping context between reports we must navigate via spa nav.
    if (!keepReportContext) {
      this.props.queryContextManager.reset();
    }

    if (shouldOpenInNewTab(triggerEvent as any)) {
      // if anchor link, browser will do the right thing by opening in new tab anyway
      if (!fromAnchorLink) {
        window.open(destinationUrl, `_blank`);
      }
    } else {
      if (this.isSpaNavigable({destinationUrlPath, currentUrlPath})) {
        const startTime = getNow();
        const newApp = this.getIronAppInfo(destinationUrlPath)!;

        if (this.props.currentProjectResource.id) {
          viewedReportFlagManager.increment(); // async, no need to wait
        }

        if (triggerEvent && triggerEvent.type === `click`) {
          // don't reload the page with new url, we'll do a spa navigate
          triggerEvent.preventDefault();
        }

        const {pathname, hash, search} = new URL(destinationUrl, getLocation().origin);
        this.props.navigate(`${pathname}${search}${hash}`);
        // wait for report shell to load and track report loaded events
        await waitUntilUiReady();
        const elapsedMs = diffFromNow(startTime);

        this.spaNavCount += 1;
        clearAllToasts();

        this.trackReportLoaded({
          reportName: getReportName(newApp.appName),
          elapsedMs,
          keepReportContext,
        });
      } else {
        // if an anchor link is clicked, let the browser do its thing
        if (!fromAnchorLink) {
          window.location.href = destinationUrl;
        }
      }
    }
  }
}
